import React, { useContext, useEffect, useState } from "react";
import { Grid, TextField, Button, Divider, InputAdornment, IconButton } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import MDBox from "components/MDBox";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MDLink from "components/MDLink";
import { VALIDATION_MESSAGES } from "utils/constants";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { addTenantAdmin, updateTenantAdmin } from "lib/Api/tenant-admin";
import { LoginFormContext } from "context/loginFormCtx";
import { formatPhoneNumber, formatToPhoneNumber, handleMasking } from "layouts/pages/tenants/tenant-admin";

const initialState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  password: "",
  confirm_password: "",
};

const phoneRegExp = /^\+?[0-9()\s-]{7,15}$/;

// RFC 5322 Official Standard for Email Validation
const emailRegExp =
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

function AddEditTenantAdminForm({ initialData = {}, isEdit = false }) {
  const { setNotification } = useContext(LoginFormContext);
  const navigate = useNavigate();
  const { db_schema, client_id } = useLocation().state || {};
  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string().matches(emailRegExp, "Invalid email").required("Email is required"),
    phone: Yup.string().matches(phoneRegExp, "Invalid phone").required("Phone is required"),
    password: Yup.string().when([], {
      is: () => !isEdit,
      then: () =>
        Yup.string()
          .required(VALIDATION_MESSAGES.PROFILE.PASSWORD.PASSWORD)
          .min(6, VALIDATION_MESSAGES.PROFILE.PASSWORD.INVALID.MIN_LENGTH)
          .matches(/[0-9]/, VALIDATION_MESSAGES.PROFILE.PASSWORD.INVALID.NUMBER)
          .matches(/[a-z]/, VALIDATION_MESSAGES.PROFILE.PASSWORD.INVALID.LOWERCASE)
          .matches(/[A-Z]/, VALIDATION_MESSAGES.PROFILE.PASSWORD.INVALID.UPPERCASE)
          .matches(/[^\w]/, VALIDATION_MESSAGES.PROFILE.PASSWORD.INVALID.SPECIAL_CHARACTER),
      otherwise: () => Yup.string().nullable().optional(),
    }),
    confirm_password: Yup.string().when(["password"], {
      is: (password) => !!password,
      then: (schema) =>
        schema
          .required("Confirm password is required")
          .oneOf([Yup.ref("password"), null], "Passwords do not match"),
      otherwise: (schema) => schema,
    }),
  });
  const { tenantId, adminId } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      ...initialState,
      ...initialData,
      password: "",
      confirm_password: "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);

        const temp = {
          first_name: values?.first_name || "",
          last_name: values?.last_name || "",
          email: values?.email || "",
          phone: values?.phone ? formatPhoneNumber(values?.phone) : "",
          client_id,
          db_schema,
        };

        if (!values.password) {
          delete temp.password;
          delete temp.confirm_password;
        } else if (values.password && values.confirm_password) {
          temp.password = values.password;
          temp.confirm_password = values.confirm_password;
        }

        let response;
        if (isEdit) {
          response = await updateTenantAdmin(adminId, temp);
        } else {
          response = await addTenantAdmin(temp);
        }

        if (response && response.status === 200) {
          setSubmitting(false);
          resetForm();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
          navigate(`/tenants/${tenantId}`);
        } else {
          setSubmitting(false);
          setNotification({
            color: "error",
            icon: "cancel",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      } catch (error) {
        setSubmitting(false);
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: error.message || "Something went wrong!",
          open: true,
        });
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name"
            name="first_name"
            value={formik.values.first_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            required
            error={Boolean(formik.touched.first_name && formik.errors.first_name)}
            helperText={formik.touched.first_name && formik.errors.first_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Last Name"
            name="last_name"
            value={formik.values.last_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            required
            error={formik.touched.last_name && Boolean(formik.errors.last_name)}
            helperText={formik.touched.last_name && formik.errors.last_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            required
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            type="email"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone Number"
            name="phone"
            value={formik.values.phone ? formatToPhoneNumber(formik.values.phone) : ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            required
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            type={showPassword ? "text" : "password"}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            required={!isEdit}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((show) => !show)} edge="end">
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Confirm Password"
            name="confirm_password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            type={showConfirmPassword ? "text" : "password"}
            error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
            helperText={formik.touched.confirm_password && formik.errors.confirm_password}
            required={!isEdit}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword((show) => !show)} edge="end">
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Divider />
      <MDBox sx={{ mt: 2 }} display="flex" justifyContent="flex-end" alignItems="center">
        <Button disabled={formik.isSubmitting} type="submit" sx={{ m: 1 }} variant="contained">
          {isEdit ? "Update Tenant Admin" : "Add Tenant Admin"}
        </Button>

        <MDLink to={`/tenants/${tenantId}`}>
          <Button variant="outlined">Cancel</Button>
        </MDLink>
      </MDBox>
    </form>
  );
}


export default AddEditTenantAdminForm;
